/**
 * portalAuthStore — "is this tab signed in through the admin portal?"
 *
 * It holds NO tokens. /admin/auth/login returns the same access + refresh
 * pair as /auth/login, and refresh tokens are single-use (each refresh
 * invalidates the previous one), so the portal and the analytics app must
 * share ONE copy of the pair — the cookies in tokenStorage. Two copies would
 * break each other on the first refresh.
 *
 * What stays here is only the portal-login flag, and it is still needed:
 * GET /admin/auth/me answers 200 for ANY valid token (a client "admin" who
 * signed into the analytics app gets role "admin", which PortalLayout
 * admits). Gating /portal on the cookies alone would let such a user into
 * the portal without ever using the portal login. The flag lives in
 * sessionStorage, so — as before — it's scoped to the tab and a closed tab
 * means signing into the portal again.
 */

import { create } from "zustand";

const STORAGE_KEY = "portal_session";

function readFlag(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    // sessionStorage can throw (e.g. blocked storage) — treat as signed out.
    return false;
  }
}

interface PortalAuthStore {
  /** True once this tab has signed in via /admin/auth/login. */
  isPortalSession: boolean;
  /** Called after a successful portal login (tokens are saved separately). */
  startSession: () => void;
  clearSession: () => void;
}

export const usePortalAuthStore = create<PortalAuthStore>()((set) => ({
  isPortalSession: readFlag(),

  startSession: () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    set({ isPortalSession: true });
  },

  clearSession: () => {
    sessionStorage.removeItem(STORAGE_KEY);
    set({ isPortalSession: false });
  },
}));
